import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { filter, tap } from 'rxjs/operators';
import { UserBookingsApiService, UserTowerGroupsApiService } from 'src/app/api/services';
import { DateFormat, StorageEnums } from 'src/shared/models/enums';
import { PaymentInfo, CustomerPaymentMethod, PurchaseDto, PaymentObject, PaymentMethodEnum } from 'src/shared/models/payment.model';
import { BookingHelperService } from './booking-helper.service';
import { DateHelperService } from './date-helper.service';
import { EventsService } from './events.service';
import { BookingStepsComponent } from 'src/app/modules/bookings/booking-steps/booking-steps.component';
import { ModalController } from '@ionic/angular/standalone';
import { StorageService } from 'src/app/core/services/storage.service';

@Injectable({
	providedIn: 'root'
})
export class PaymentsHelperService {
	private paymentInfo: PaymentInfo;
	private stripeReady: BehaviorSubject<any> = new BehaviorSubject<any>(null);
	public $stripeReady: Observable<any>;

	constructor(private http: HttpClient,
				// private ppService: PaypalHelperService,
				private bookingService: UserBookingsApiService,
				private eventsService: EventsService,
				private bookingHelper: BookingHelperService,
				private groupsApiService: UserTowerGroupsApiService,
				private modalCtrl: ModalController,
				private storage: StorageService,
				private router: Router) {
		this.$stripeReady = this.stripeReady.asObservable()
			.pipe(
				filter(v => v !== null)
			);
	}

	setStripeReady(data) {
		this.stripeReady.next(data);
	}

	getCardBrandsImage(brand: string) {
		let imgUrl;
		switch (brand.toLowerCase()) {
			case 'visa':
				imgUrl = 'assets/icon/credit-cards/visa.svg';
				break;

			case 'mastercard':
				imgUrl = 'assets/icon/credit-cards/mastercard.svg';
				break;

			case 'amex':
				imgUrl = 'assets/icon/credit-cards/amer-bank.svg';
				break;
			case 'paypal':
				imgUrl = 'assets/icon/credit-cards/paypal.svg';
				break;
			case 'giropay':
				imgUrl = 'assets/icon/credit-cards/giropay.svg';
				break;
			case 'twint':
				imgUrl = 'assets/icon/credit-cards/twint.svg';
				break;

			default: {
				imgUrl = 'assets/icon/credit-cards/default.jpg';
				break;
			}
		}

		return imgUrl;
	}

	get paymentData() {
		return this.paymentInfo;
	}

	set paymentData(data: PaymentInfo) {
		this.paymentInfo = data;
	}

	async backToBooking(toPayCardInfo: PaymentInfo) {
		await this.modalCtrl.dismiss();
		const booking = toPayCardInfo.bookingId ? await firstValueFrom(this.bookingService.getUserActiveBookings({bookingId: toPayCardInfo.bookingId})): null;
		const group = (await firstValueFrom(this.groupsApiService.getUserTowerGroups())).find(v => v.id === toPayCardInfo.groupId);
		const modal = await this.modalCtrl.create({
			id: 'booking-details',
			component: BookingStepsComponent,
			cssClass: 'auto-height',
			componentProps: {
				booking: {
					...booking,
					sharedBookingsAccessCount: booking?.sharesCount?.total,
					activeSharedBookingsAccessCount: booking?.sharesCount?.accepted
				},
				group: group,
				currentStep: toPayCardInfo.type === PaymentObject.Subscription ? 8: 2
			}
		});

		modal.present();
	}

	isPaymentMethodSupported(method: PaymentMethodEnum): boolean {
		return this.paymentInfo.paymentMethods && this.paymentInfo.paymentMethods.includes(method);
	}

	createMethod(type: PaymentMethodEnum, fullName = null) {
		let method: CustomerPaymentMethod;
		let brandImg;
		let brand;
		let additionalText;
		let last4 = '';

		switch (type) {
			case PaymentMethodEnum.PAYPAL:
				brand = 'PayPal'
				brandImg = this.getCardBrandsImage('paypal');
				last4 = fullName
				break;
			case PaymentMethodEnum.STRIPE_CARD:
				brand = 'Credit Card';
				brandImg = this.getCardBrandsImage('default');

				break;
			// case PaymentMethodEnum.STRIPE_GIROPAY:
			// 	brand = 'Giropay';
			// 	brandImg = this.getCardBrandsImage('giropay');
			// 	break;

			case PaymentMethodEnum.PAYREXX_TWINT:
				brand = 'Twint';
				brandImg = this.getCardBrandsImage('twint');
				break;
		}

		method = {
			brand,
			last4,
			brandImg,
			additionalText,
			id: '',
			type
		}

		return method;
	}

	async openPaymentWindow(url, action) {
		await this.storage.setItem(StorageEnums.SUB_START_DATE, this.paymentInfo.subscriptionStartDate);
		window.open(url, "_self");
		let returnurl = document.location.origin + '/app/home';
	}

	terminateBooking(bookingId, booking) {
		return this.bookingService.terminateBooking({
			bookingId: bookingId,
		}).pipe(
			tap(() => {
				this.eventsService.refreshBookings();
				this.bookingHelper.handleTerminateBooking(bookingId);
				// this.analyticsService.logEvent(
				// 	AnalyticsEvents.TerminateBooking,
				// 	booking);
			})
		);
	}

	createSubscription(paymentId, groupId, startDate?: string) {
		return this.groupsApiService.subscribe({
			id: groupId,
			body: {
				paymentId,
				startDate: new Date(startDate || new Date()).toISOString()
			}
		}).pipe(
			tap((s) => {
				// this.analyticsService.logEvent(AnalyticsEvents.CreateSubscription, s);
				this.eventsService.refreshBookings();
				this.eventsService.updateSubs();
			})
		);
	}

	getPurchases() {
		this.http.get('https://test.v-locker.ch/msp/api/purchases/')
			.subscribe((r: any[]) => {
				let filtered = r.filter(p => !p.is_paid && Number(p.total_price) > 1);
				console.log('filtered', filtered);
				if (filtered.length !== 0) {
					let forPay = filtered[0];
					this.preparePaymentDataForPurchase(forPay);
				}
			})
	}

	preparePaymentDataForPurchase(purchase: PurchaseDto) {
		this.paymentData = {
			amountToPay: Number(purchase.total_price),
			type: PaymentObject.Purchase,
			currency: purchase.currency,
			purchase: {
				productsCounts: purchase.orders.length,
				purchaseDate: DateHelperService.convertDate(purchase.time_created, 'default', DateFormat.ForDisplay),
				id: purchase.id
			}
		}

		this.router.navigateByUrl('payment');
	}
}
